import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { setDisplayCurrency } from "@/utils/formatCurrency";
import {
  fetchBusinessProfile,
  fetchBusinessSettings,
  fetchBusinessUsers,
  storeHandle,
} from "@/services/businessApi";

/**
 * The signed-in user's business profile, settings and staff directory.
 *
 * Every query is keyed by businessId so switching accounts can never serve the
 * previous business's cache.
 */
export function useBusiness() {
  const { businessId } = useAuth();

  const profile = useQuery({
    queryKey: ["business", businessId, "profile"],
    queryFn: () => fetchBusinessProfile(businessId),
    enabled: Boolean(businessId),
    staleTime: 5 * 60 * 1000,
  });

  const settings = useQuery({
    queryKey: ["business", businessId, "settings"],
    queryFn: () => fetchBusinessSettings(businessId),
    enabled: Boolean(businessId),
    staleTime: 5 * 60 * 1000,
  });

  const users = useQuery({
    queryKey: ["business", businessId, "users"],
    queryFn: () => fetchBusinessUsers(businessId),
    enabled: Boolean(businessId),
    staleTime: 5 * 60 * 1000,
  });

  const currency = settings.data?.currency || "KES";

  // Most money is rendered by pure components with no access to this hook, so
  // formatCurrency keeps the resolved code as its default.
  useEffect(() => {
    setDisplayCurrency(currency);
  }, [currency]);

  // Null until both docs land, so callers can wait rather than route to a
  // half-built URL like /store/undefined/home.
  const handle =
    profile.data && settings.data ? storeHandle(profile.data, settings.data) : null;

  return {
    businessId,
    storeHandle: handle,
    business: profile.data ?? null,
    settings: settings.data ?? null,
    users: users.data ?? {},
    currency,
    isLoading: profile.isLoading || settings.isLoading || users.isLoading,
    isError: profile.isError || settings.isError || users.isError,
  };
}

export default useBusiness;
